import { Alert, Box, Link, List, ListItem, Typography } from '@mui/material';
import React, { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import config from '../../../config/environment';
import { formatRecordDate } from '../../../library/dateTimeHelper';
import ResourcesIds from '../../../resources/ResourcesIds';
import {
    documentStyles,
    eyebrowStyles,
    headerBoxStyles,
    introStyles,
    lastUpdatedStyles,
    linkListStyles,
    listItemStyles,
    listStyles,
    operatorGridStyles,
    operatorLabelStyles,
    operatorRowStyles,
    operatorValueStyles,
    pageWrapperStyles,
    paragraphStyles,
    placeholderAlertStyles,
    sectionStyles,
    sectionTitleStyles,
    titleStyles,
    tocCardStyles,
    tocLinkStyles,
    tocListStyles,
    tocTitleStyles,
} from './termsAndConditions.styles';
import { TermsBlock, TermsSection, termsSections } from './termsContent';
import { anpcSalUrl, anpcUrl, isOperatorIncomplete, termsLastUpdated, termsOperator } from './termsOperator';

/**
 * The platform's terms of use, as a signed-out visitor reads them.
 *
 * **These are Carefull's terms, not a clinic's**, and the split is the whole point rather
 * than a detail. The page is reached from the registration form and from an activation
 * email, before any session names a clinic, so it cannot describe one clinic's cancellation
 * policy or prices without describing them wrongly for every other clinic on the platform.
 * What it says about care is therefore deliberately thin and points at the clinic's own
 * documents -- the informed consent signed at the first evaluation, the price list, the
 * internal rules -- which are what actually govern the treatment.
 *
 * Everything it says about the *app* is checked against what the app does, and a few
 * clauses exist because the code behaves in a way a patient would not otherwise guess:
 * a no-show consumes no session, a package's validity date is advisory rather than
 * enforced, and no money moves through the platform at all.
 *
 * The text has not been through a lawyer. It is written to be reviewed, not to be relied
 * on as drafted.
 */
export const TermsAndConditions = (): JSX.Element => {
    const { i18n, t: translate } = useTranslation();

    const operatorRows: { labelId: string; value: string }[] = [
        { labelId: ResourcesIds.TERMS_CONTACT_COMPANY, value: termsOperator.legalName },
        { labelId: ResourcesIds.TERMS_CONTACT_CUI, value: termsOperator.cui },
        { labelId: ResourcesIds.TERMS_CONTACT_REG, value: termsOperator.tradeRegister },
        { labelId: ResourcesIds.TERMS_CONTACT_ADDRESS, value: termsOperator.address },
        { labelId: ResourcesIds.TERMS_CONTACT_EMAIL, value: termsOperator.email },
    ].filter((row) => row.value.trim() !== '');

    const renderBlock = (block: TermsBlock, key: string): JSX.Element => {
        switch (block.kind) {
            case 'paragraph':
                return (
                    <Typography key={key} variant="body1" sx={paragraphStyles}>
                        {translate(block.textId)}
                    </Typography>
                );

            case 'list':
                return (
                    <List key={key} sx={listStyles}>
                        {block.itemIds.map((itemId: string) => (
                            <ListItem key={itemId} disableGutters sx={listItemStyles}>
                                {translate(itemId)}
                            </ListItem>
                        ))}
                    </List>
                );

            case 'consumerLinks':
                return (
                    <Box key={key} sx={linkListStyles}>
                        <Link href={anpcUrl} target="_blank" rel="noopener noreferrer">
                            {translate(ResourcesIds.TERMS_LINK_ANPC)}
                        </Link>
                        <Link href={anpcSalUrl} target="_blank" rel="noopener noreferrer">
                            {translate(ResourcesIds.TERMS_LINK_SAL)}
                        </Link>
                    </Box>
                );

            case 'operatorDetails':
                // Drawn only for the fields somebody has actually filled in, the same rule
                // the footer applies to a clinic's social links: an empty block is better
                // than five labels with nothing beside them.
                return operatorRows.length === 0 ? (
                    <React.Fragment key={key} />
                ) : (
                    <Box key={key} sx={operatorGridStyles}>
                        {operatorRows.map((row) => (
                            <Box key={row.labelId} sx={operatorRowStyles}>
                                <Typography variant="body2" sx={operatorLabelStyles}>
                                    {translate(row.labelId)}
                                </Typography>
                                <Typography variant="body2" sx={operatorValueStyles}>
                                    {row.value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                );
        }
    };

    return (
        <Box sx={pageWrapperStyles}>
            <Box sx={documentStyles}>
                <Box sx={headerBoxStyles}>
                    <Typography sx={eyebrowStyles}>{translate(ResourcesIds.TERMS_EYEBROW)}</Typography>
                    <Typography variant="h3" component="h1" sx={titleStyles}>
                        {translate(ResourcesIds.TERMS_TITLE)}
                    </Typography>
                    <Typography variant="body2" sx={lastUpdatedStyles}>
                        {translate(ResourcesIds.TERMS_LAST_UPDATED, {
                            // A bare date parses as UTC and would read as the day before in
                            // a western zone; with a time on it, it is read as local.
                            date: formatRecordDate(`${termsLastUpdated}T00:00:00`, i18n),
                        })}
                    </Typography>
                    <Typography variant="body1" sx={introStyles}>
                        {translate(ResourcesIds.TERMS_INTRO)}
                    </Typography>
                </Box>

                {/* Never in a production build: this is a note to whoever is filling the
                    document in, not to a patient. */}
                {config.isDevelopment && isOperatorIncomplete(termsOperator) && (
                    <Alert severity="warning" sx={placeholderAlertStyles}>
                        {translate(ResourcesIds.TERMS_DEV_PLACEHOLDER_WARNING)}
                    </Alert>
                )}

                <Box sx={tocCardStyles}>
                    <Typography variant="h6" sx={tocTitleStyles}>
                        {translate(ResourcesIds.TERMS_TOC_TITLE)}
                    </Typography>
                    <Box component="ol" sx={tocListStyles}>
                        {termsSections.map((section: TermsSection, index: number) => (
                            <li key={section.id}>
                                <Link href={`#${section.id}`} sx={tocLinkStyles}>
                                    {index + 1}. {translate(section.titleId)}
                                </Link>
                            </li>
                        ))}
                    </Box>
                </Box>

                {termsSections.map((section: TermsSection, index: number) => (
                    <Box key={section.id} id={section.id} component="section" sx={sectionStyles}>
                        <Typography variant="h5" component="h2" sx={sectionTitleStyles}>
                            {index + 1}. {translate(section.titleId)}
                        </Typography>
                        {section.blocks.map((block: TermsBlock, blockIndex: number) =>
                            renderBlock(block, `${section.id}-${blockIndex}`)
                        )}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};
